/**
 * @file Require a valid album folder (via matched :albumFolderId).
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Redirect, withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';

import { AlbumFolderWithCreatorBrandDetails } from '../../graphql/queries/album-folder';
import { openAlert as openAlertAction } from '../../actions/modal-actions';
import { ALL_PHOTOS_MODE } from '../../constants/album-folder';

/**
 * Higher order component for the require valid album folder functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {React.Component} The composed component.
 */
const WithValidAlbumFolderHOC = WrappedComponent => {
  const propTypes = {
    albumFolder: PropTypes.shape({
      id: PropTypes.string,
    }),
    loading: PropTypes.bool.isRequired,
    openAlert: PropTypes.func,
    history: PropTypes.shape({
      push: PropTypes.func,
    }),
  };

  const withValidAlbumFolderHOC = ({
    albumFolder,
    loading,
    openAlert,
    history,
    ...props
  }) => {
    useEffect(() => {
      if (
        albumFolder &&
        !albumFolder.joins_enabled &&
        albumFolder.all_photos_mode !== ALL_PHOTOS_MODE.PRIVATE
      ) {
        openAlert({
          type: 'error',
          title: 'Restricted Access',
          message: (
            <>
              <p>Whoops! You aren&rsquo;t able to join this gallery!</p>
              {albumFolder.is_web_gallery && (
                <p>You can still browse the photos on the free web gallery.</p>
              )}
            </>
          ),
          btnLabel: albumFolder.is_web_gallery
            ? 'Go to web gallery'
            : 'Go to my content',
          onDismiss: () => {
            history.push(
              albumFolder.is_web_gallery
                ? `/galleries/view/folder/${albumFolder.id}`
                : '/app/shutterbug',
            );
          },
        });
      }
    }, [albumFolder, loading]);

    // Move this to a Redirect since we need to redirect for the `/next?` prop
    // to be properly handled by the server if the user is unauthenticated
    if (
      albumFolder &&
      !albumFolder.joins_enabled &&
      albumFolder.all_photos_mode === ALL_PHOTOS_MODE.PRIVATE
    ) {
      return <Redirect to={`/s/all-photos-access/${albumFolder.id}`} />;
    }

    return !loading && !albumFolder ? (
      <Redirect to="/not-found" />
    ) : (
      <WrappedComponent
        albumFolder={albumFolder}
        loading={loading}
        openAlert={openAlert}
        history={history}
        {...props}
      />
    );
  };

  withValidAlbumFolderHOC.propTypes = propTypes;

  return withValidAlbumFolderHOC;
};

/**
 * Higher order container for the require valid album folder functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {Function} The HOC container.
 */
const RequireValidAlbumFolder = WrappedComponent => {
  const WithValidAlbumFolder = WithValidAlbumFolderHOC(WrappedComponent);

  const withAlbumFolderData = graphql(AlbumFolderWithCreatorBrandDetails, {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumFolderId,
      },
    }),
    props: ({ data: { albumFolder, loading } }) => ({
      albumFolder,
      loading,
    }),
  });

  const mapDispatchToProps = {
    openAlert: openAlertAction,
  };

  return compose(
    withRouter,
    connect(null, mapDispatchToProps),
    withAlbumFolderData,
  )(WithValidAlbumFolder);
};

export { WithValidAlbumFolderHOC };
export default RequireValidAlbumFolder;
